import { QRCodeSVG } from 'qrcode.react';
import { Icon, IconName } from './Icon';
import { useNow } from '../hooks/useTimers';
import { AttendanceResponse, Challenge, QrResponse, QrType } from '../types';
import { fmtDateLong, fmtStopwatch, fmtTimeRange } from '../utils/format';

interface QrSheetProps {
  mode: QrType;
  challenge: Challenge;
  qr: QrResponse | null;
  attendance: AttendanceResponse | null;
  onClose: () => void;
  onRegenerate: () => void;
}

/**
 * Check-in or check-out QR panel. The QR text comes from the server; the app
 * only draws it. Rejections by the validator come in `lastScan` / `locationCheck`.
 */
export function QrSheet({
  mode,
  challenge,
  qr,
  attendance,
  onClose,
  onRegenerate,
}: QrSheetProps) {
  const isOut = mode === 'CHECK_OUT';
  const now = useNow();

  const remaining = qr
    ? Math.max(0, Math.floor((new Date(qr.expiresAt).getTime() - now) / 1000))
    : 0;
  const expired = Boolean(qr) && remaining === 0;
  const pct =
    qr && qr.validForSeconds
      ? Math.round((remaining / qr.validForSeconds) * 100)
      : 0;

  const address = challenge.location.address || challenge.location.name || '';
  const lastScan = attendance?.lastScan;

  let chip: { tone: string; icon: IconName; text: string } = {
    tone: 'neutral',
    icon: 'pin',
    text: `Válido somente em: ${address}`,
  };
  if (attendance?.locationCheck === 'MATCH') {
    chip = {
      tone: 'success',
      icon: 'check',
      text: 'Você está no local do desafio',
    };
  } else if (attendance?.locationCheck === 'MISMATCH') {
    chip = {
      tone: 'warn',
      icon: 'warning',
      text: 'Você não está no local do desafio',
    };
  }

  let status = {
    tone: 'wait',
    text: isOut
      ? 'Aguardando o gestor registrar sua saída'
      : 'Aguardando o gestor registrar sua entrada',
  };
  if (expired || lastScan?.reason === 'EXPIRED') {
    status = {
      tone: 'warn',
      text: 'Este QR Code expirou. Gere um novo para continuar.',
    };
  } else if (lastScan?.reason === 'WRONG_LOCATION') {
    status = {
      tone: 'warn',
      text: `Este QR Code só vale em ${address}. O validador o recusou neste local.`,
    };
  } else if (lastScan?.reason === 'OUT_OF_WINDOW') {
    status = {
      tone: 'warn',
      text: `Fora do horário do desafio (${fmtTimeRange(challenge.startsAt, challenge.endsAt)}).`,
    };
  }
  const hasProblem = status.tone === 'warn';

  const steps = [
    { label: 'Entrada', state: isOut ? 'done' : 'current' },
    { label: 'Permanência', state: isOut ? 'done' : 'todo' },
    { label: 'Saída', state: isOut ? 'current' : 'todo' },
  ];

  return (
    <div
      className="pv-sheet-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pv-sheet-title"
    >
      <div className="pv-sheet-context" aria-hidden="true">
        <span>DESAFIO</span>
        <strong>{challenge.title}</strong>
        <span>
          {fmtDateLong(challenge.startsAt)} ·{' '}
          {fmtTimeRange(challenge.startsAt, challenge.endsAt)}
        </span>
      </div>

      <div className="pv-sheet">
        <button
          type="button"
          className="pv-sheet__close"
          onClick={onClose}
          aria-label="Fechar"
        >
          <Icon name="close" size={22} />
        </button>

        <ol className="pv-stepper" aria-label="Etapas do desafio">
          {steps.map((s, i) => (
            <li key={s.label} className={`is-${s.state}`}>
              <span className="pv-stepper__dot">
                {s.state === 'done' ? '✓' : i + 1}
              </span>
              {s.label}
            </li>
          ))}
        </ol>

        <div className="pv-center">
          <h1 id="pv-sheet-title" className="pv-h2">
            {isOut ? 'QR Code de saída' : 'QR Code de entrada'}
          </h1>
          <p className="pv-muted">
            {isOut
              ? 'Mostre esta tela ao seu gestor para registrar sua saída e receber seus pontos.'
              : 'Mostre esta tela ao seu gestor para registrar sua entrada no local.'}
          </p>
        </div>

        <div
          className={`pv-qr${hasProblem ? ' is-problem' : ''}${expired ? ' is-expired' : ''}`}
        >
          {qr ? (
            <QRCodeSVG
              value={qr.qrPayload}
              size={200}
              level="M"
              aria-label="QR Code de registro de presença"
              role="img"
            />
          ) : (
            <div
              className="pv-skeleton"
              style={{ width: 200, height: 200 }}
              aria-label="Gerando QR Code"
            />
          )}
        </div>

        <span className={`pv-chip pv-chip--${chip.tone}`}>
          <Icon name={chip.icon} size={16} />
          {chip.text}
        </span>

        {qr && (
          <div className="pv-validity">
            <div className="pv-validity__row">
              <span className="pv-muted">
                <Icon name="clock" size={16} />{' '}
                {expired ? 'QR Code expirado' : 'Válido por mais'}
              </span>
              <strong>{fmtStopwatch(remaining)}</strong>
            </div>
            <div className="pv-progress" aria-hidden="true">
              <div style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}

        <div className={`pv-status pv-status--${status.tone}`} role="status">
          <span className="pv-status__dot" aria-hidden="true" />
          {status.text}
        </div>

        {expired || lastScan?.reason === 'EXPIRED' ? (
          <button
            type="button"
            className="pv-btn pv-btn--primary pv-btn--block"
            onClick={onRegenerate}
          >
            Gerar novo QR Code
          </button>
        ) : (
          <button
            type="button"
            className="pv-btn pv-btn--primary pv-btn--block"
            onClick={onClose}
          >
            Fechar
          </button>
        )}
      </div>
    </div>
  );
}
